/**
 * QuickWins Component
 * 
 * Displays actionable checklist of simple tax-saving actions.
 * 
 * Features:
 * - Progress header (X of Y completed)
 * - Visual progress bar
 * - Interactive checklist with checkboxes
 * - Completed items styled with checkmark
 * - Pending items with empty checkbox
 * - Motivational footer message
 * - Responsive design
 * 
 * Created: 2025-01-16 (Dashboard Redesign Phase 2)
 * Phase: Dashboard Redesign - Phase 2: Content Cards
 */

import React from 'react';

const QuickWins = ({ actions, onToggle }) => {
  const completedCount = (actions || []).filter(action => action.completed).length;
  const totalCount = (actions || []).length;
  const progressPercentage = Math.round((completedCount / totalCount) * 100);

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-900">Quick Wins</h3>
          <span className="text-sm font-semibold text-blue-600">
            {completedCount} of {totalCount} completed
          </span>
        </div>
        <p className="text-sm text-gray-600">
          Simple actions to maximize your tax savings
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-500">Progress</span>
          <span className="text-xs font-semibold text-gray-700">{progressPercentage}%</span>
        </div>
      </div>

      {/* Checklist */}
      <div className="space-y-3">
        {(actions || []).map((action) => (
          <label
            key={action.id}
            className={`
              flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-all
              ${action.completed 
                ? 'bg-green-50 hover:bg-green-100' 
                : 'hover:bg-gray-50'
              }
            `}
          >
            {/* Checkbox */}
            <div className="flex-shrink-0 mt-0.5">
              {action.completed ? (
                <div className="w-5 h-5 rounded bg-green-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-5 h-5 rounded border-2 border-gray-300 hover:border-gray-400 transition-colors" />
              )}
            </div>

            {/* Action Text */}
            <div className="flex-1">
              <span 
                className={`
                  text-sm
                  ${action.completed 
                    ? 'text-gray-500 line-through' 
                    : 'text-gray-900 font-medium'
                  }
                `}
              >
                {action.text}
              </span>
            </div>

            {/* Hidden Checkbox for Accessibility */}
            <input
              type="checkbox"
              checked={action.completed}
              onChange={() => onToggle && onToggle(action.id)}
              className="sr-only"
              aria-label={action.text}
            />
          </label>
        ))}
      </div>

      {/* Footer Message */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-start space-x-2 bg-blue-50 rounded-lg p-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-xs text-blue-900">
            <span className="font-semibold">Keep Going!</span> Complete these simple actions to maximize your tax savings potential.
          </p>
        </div>
      </div>
    </div>
  );
};

// Default props
QuickWins.defaultProps = {
  actions: [
    {
      id: 1,
      text: 'Set up business bank account',
      completed: true,
    },
    {
      id: 2,
      text: 'Install expense-tracking app',
      completed: true,
    },
    {
      id: 3,
      text: 'Organize receipts folder',
      completed: true,
    },
    {
      id: 4,
      text: 'Schedule quarterly tax payments',
      completed: false,
    },
    {
      id: 5,
      text: 'Review business credit cards',
      completed: false,
    },
    {
      id: 6,
      text: 'Update mileage log system',
      completed: false,
    },
    {
      id: 7,
      text: 'Document home office setup',
      completed: false,
    },
  ],
  onToggle: null, // No-op by default (future: save to backend)
};

export default QuickWins;
